import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import PageMeta from "../components/PageMeta";
import { trackSiteEvent } from "../lib/analytics";
import { APP_STORE_URL, PLAY_STORE_URL, ANDROID_LIVE } from "../lib/appStores";
import { Button } from "../components/ui/button";
import { MapPin, Clock, NavigationArrow, CaretRight, CalendarBlank, DeviceMobile } from "@phosphor-icons/react";

const SITE = "https://thetrucktrackr.com";

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
};
const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
};

function StopCard({ r, market }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex gap-3 items-start" data-testid={`market-stop-${r.truck_slug || r.truck_id}`}>
      {r.logo_pin_url && <img src={r.logo_pin_url} alt={`${r.truck_name} logo`} loading="lazy" className="h-12 w-12 rounded-full object-cover flex-none border border-border" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {r.truck_slug ? (
            <Link to={`/t/${r.truck_slug}`} onClick={() => trackSiteEvent("market_truck_click", { market: market.slug, truck: r.truck_slug })} className="font-semibold hover:text-primary transition-colors" data-testid={`market-stop-link-${r.truck_slug}`}>{r.truck_name}</Link>
          ) : <span className="font-semibold">{r.truck_name}</span>}
          {r.live_now && <span className="text-[10px] font-bold uppercase tracking-wide bg-green-100 text-green-700 rounded-full px-2 py-0.5">Open now</span>}
        </div>
        {r.event_name && <div className="text-sm text-muted-foreground truncate">{r.event_name}</div>}
        {r.address && <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={14} className="flex-none" /><span className="truncate">{r.address}</span></div>}
        <div className="text-sm flex items-center gap-1 mt-0.5"><Clock size={14} className="text-muted-foreground" /> {fmtTime(r.starts_at)} – {fmtTime(r.ends_at)}</div>
      </div>
      {r.lat != null && (
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}`}
          target="_blank" rel="noopener noreferrer"
          onClick={() => trackSiteEvent("market_directions_click", { market: market.slug, truck: r.truck_slug })}
          className="flex-none inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1"
          data-testid={`market-directions-${r.truck_slug || r.truck_id}`}
        >
          <NavigationArrow size={15} /> Directions
        </a>
      )}
    </div>
  );
}

export default function FoodTrucksMarket({ slug }) {
  const params = useParams();
  const citySlug = slug || params.citySlug || "lancaster-pa";
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cuisineFilter, setCuisineFilter] = useState(null);
  const [foundingPromo, setFoundingPromo] = useState(null);
  const [preloaded, setPreloaded] = useState([]);

  useEffect(() => {
    let live = true;
    api.get(`/markets/${encodeURIComponent(citySlug)}`)
      .then(({ data }) => { if (live) { setData(data); trackSiteEvent("market_page_view", { market: citySlug }); } })
      .catch(() => live && setNotFound(true));
    // Founding Pro launch promo counts (only markets with an expansion
    // city + slots configured return active=true).
    api.get(`/preloaded-trucks/stats?city=${encodeURIComponent(citySlug)}`)
      .then(({ data }) => { if (live && data?.founding_promo_active) setFoundingPromo(data); })
      .catch(() => {});
    // Preloaded (claimable) trucks for this market, if any were imported.
    api.get(`/preloaded-trucks?city=${encodeURIComponent(citySlug)}&limit=24`)
      .then(({ data }) => { if (live && Array.isArray(data)) setPreloaded(data); })
      .catch(() => {});
    return () => { live = false; };
  }, [citySlug]);

  const jsonLd = useMemo(() => {
    if (!data) return null;
    const m = data.market;
    const url = `${SITE}/food-trucks/${m.slug}`;
    return {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "WebPage", "@id": url, url, name: m.seo_title, description: m.meta_description, isPartOf: { "@type": "WebSite", name: "TruckTrackr", url: SITE } },
        { "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Food Trucks", item: `${SITE}/profiles` },
          { "@type": "ListItem", position: 3, name: m.market_name, item: url },
        ]},
        ...(data.trucks.length ? [{ "@type": "ItemList", name: `Food trucks serving ${m.market_name}`, numberOfItems: data.trucks.length,
          itemListElement: data.trucks.slice(0, 25).map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.truck_name, url: t.truck_slug ? `${SITE}/t/${t.truck_slug}` : undefined })) }] : []),
      ],
    };
  }, [data]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="text-2xl font-bold mb-2">Market not found</h1>
      <p className="text-muted-foreground mb-6">We don't have a landing page for this area yet.</p>
      <Link to="/map"><Button>Open the live map</Button></Link>
    </div>
  );
  if (!data) return <div className="max-w-5xl mx-auto px-4 py-16 text-muted-foreground">Loading food trucks…</div>;

  const m = data.market;
  // All map/directory links stay scoped to THIS market's area.
  const mapHref = `/map?lat=${m.latitude}&lng=${m.longitude}&zoom=${m.map_zoom || 10}&label=${encodeURIComponent(m.market_name)}`;
  const dirHref = `/profiles?market=${m.slug}`;
  const filteredTrucks = cuisineFilter ? data.trucks.filter((t) => t.cuisine_canonical.includes(cuisineFilter)) : data.trucks;
  const upcomingByDay = data.upcoming.reduce((acc, r) => { const k = dayLabel(r.starts_at); (acc[k] = acc[k] || []).push(r); return acc; }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="market-landing-page">
      <PageMeta
        title={m.seo_title}
        description={m.meta_description}
        canonical={`${SITE}/food-trucks/${m.slug}`}
        jsonLd={jsonLd}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5" data-testid="market-breadcrumbs">
        <Link to="/" className="hover:text-foreground">Home</Link><CaretRight size={12} />
        <Link to="/profiles" className="hover:text-foreground">Food Trucks</Link><CaretRight size={12} />
        <span className="text-foreground font-medium">{m.market_name}</span>
      </nav>

      <h1 className="text-4xl sm:text-5xl font-bold font-display mb-3">Food Trucks in {m.in_phrase ? `${m.in_phrase}, ${m.state_abbreviation}` : m.market_name}</h1>
      <p className="text-base text-muted-foreground max-w-3xl mb-2">{m.intro}</p>
      {data.updated_at && (
        <p className="text-xs text-muted-foreground mb-8" data-testid="market-freshness">
          {m.city} food truck schedules updated from TruckTrackr listings — latest posting {new Date(data.updated_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}.
        </p>
      )}

      {foundingPromo && (
        <section
          className="mb-10 rounded-2xl border border-cyan-300 dark:border-cyan-700 bg-gradient-to-r from-cyan-50 via-sky-50 to-blue-50 dark:from-cyan-950/40 dark:via-sky-950/40 dark:to-blue-950/40 p-6"
          data-testid="market-founding-promo"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300 mb-1">
                {foundingPromo.founding_promo_label || "Founding Pro launch promo"}
              </div>
              <h2 className="text-lg md:text-lg font-semibold font-display mb-1">
                Own a food truck in {m.in_phrase || m.city}?
              </h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                The first {foundingPromo.founding_slots_total} trucks to join TruckTrackr in {m.in_phrase || m.city} get{" "}
                <span className="font-semibold text-foreground">
                  {foundingPromo.founding_term_days ? "Pro free for a full year" : "Pro free — for life"}
                </span>. Live map pin, schedules,
                follower notifications, marketing tools, the works.
              </p>
            </div>
            <div className="flex-none flex flex-col items-start sm:items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-cyan-600 text-white text-xs font-bold px-3 py-1"
                data-testid="market-founding-slots-left"
              >
                {foundingPromo.founding_slots_available} of {foundingPromo.founding_slots_total} founding spots left
              </span>
              <Link
                to={`/signup?role=owner&home_base=${encodeURIComponent(m.market_name)}`}
                onClick={() => trackSiteEvent("market_founding_cta", { market: m.slug })}
              >
                <Button className="rounded-full" data-testid="market-founding-cta">Claim a founding spot</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="mb-10">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-3">Food Trucks Serving in {m.in_phrase || m.city} Today</h2>
        {data.today.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">{data.today.map((r) => <StopCard key={r.truck_id + r.starts_at} r={r} market={m} />)}</div>
        ) : (
          <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground" data-testid="market-today-empty">
            No {m.city} food trucks have posted a public stop for today yet. Check upcoming {m.city} stops below or{" "}
            <Link to={mapHref} onClick={() => trackSiteEvent("market_map_click", { market: m.slug })} className="text-primary hover:underline">explore the live map</Link>.
          </div>
        )}
      </section>

      <section className="mb-10">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-3">Upcoming Food Trucks in {m.in_phrase || m.city}</h2>
        {data.upcoming.length > 0 ? (
          Object.entries(upcomingByDay).map(([day, rows]) => (
            <div key={day} className="mb-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><CalendarBlank size={14} /> {day}</h3>
              <div className="grid gap-3 sm:grid-cols-2">{rows.map((r) => <StopCard key={r.truck_id + r.starts_at} r={r} market={m} />)}</div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground" data-testid="market-upcoming-empty">
            No upcoming {m.city} stops have been posted yet this week. Browse the{" "}
            <Link to={dirHref} className="text-primary hover:underline">{m.city} truck directory</Link> or{" "}
            <Link to={mapHref} className="text-primary hover:underline">open the live map</Link>.
          </div>
        )}
      </section>

      {data.cuisines.length > 0 && (
        <section className="mb-10">
          <h2 className="text-lg md:text-lg font-semibold font-display mb-3">Explore {m.city} Food Trucks by Cuisine</h2>
          <div className="flex flex-wrap gap-2" data-testid="market-cuisine-chips">
            {data.cuisines.map(([c, n]) => (
              <button
                key={c}
                onClick={() => setCuisineFilter(cuisineFilter === c ? null : c)}
                className={`text-sm rounded-full border px-3 py-1.5 transition-colors ${cuisineFilter === c ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}
                data-testid={`market-cuisine-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c} <span className="opacity-60">({n})</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="mb-10">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-3">Food Trucks Serving {m.market_name}{cuisineFilter ? ` — ${cuisineFilter}` : ""}</h2>
        {filteredTrucks.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="market-truck-directory">
            {filteredTrucks.map((t) => (
              <Link
                key={t.truck_id}
                to={t.truck_slug ? `/t/${t.truck_slug}` : "/profiles"}
                onClick={() => trackSiteEvent("market_truck_click", { market: m.slug, truck: t.truck_slug })}
                className="rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors block"
                data-testid={`market-truck-${t.truck_slug || t.truck_id}`}
              >
                <div className="flex items-center gap-3">
                  {t.logo_url && <img src={t.logo_url} alt={`${t.truck_name} logo`} loading="lazy" className="h-11 w-11 rounded-full object-cover border border-border flex-none" />}
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{t.truck_name}</div>
                    {t.cuisine_canonical.length > 0 && <div className="text-xs text-muted-foreground truncate">{t.cuisine_canonical.join(" · ")}</div>}
                  </div>
                </div>
                {t.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{t.description}</p>}
                {t.service_area && <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><MapPin size={12} /> {t.service_area}</p>}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="market-directory-empty">
            {cuisineFilter ? `No ${cuisineFilter} trucks in ${m.in_phrase || m.city} yet — ` : `No ${m.city} trucks found — `}
            <Link to={dirHref} className="text-primary hover:underline">browse the {m.city} directory</Link>.
          </p>
        )}
      </section>

      {preloaded.length > 0 && (
        <section className="mb-10" data-testid="market-claim-section">
          <h2 className="text-lg md:text-lg font-semibold font-display mb-1">Is Your Truck Listed? Claim It Free</h2>
          <p className="text-sm text-muted-foreground max-w-3xl mb-4">
            We&apos;ve preloaded local trucks across {m.in_phrase || m.city} so customers can find them from day one.
            Claiming is free and takes a minute — you get full control of your profile, schedule and live map pin
            {foundingPromo ? `, and founding trucks get Pro free${foundingPromo.founding_term_days ? " for a year" : " for life"}` : ""}.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {preloaded.map((t) => (
              <Link
                key={t.id}
                to={`/food-trucks/${citySlug}/${t.slug}`}
                onClick={() => trackSiteEvent("market_claim_click", { market: m.slug, truck: t.slug })}
                className="rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors block"
                data-testid={`market-claim-${t.slug}`}
              >
                <div className="flex items-center gap-3">
                  {t.logo_url && <img src={t.logo_url} alt={`${t.truck_name} logo`} loading="lazy" className="h-11 w-11 rounded-full object-cover border border-border flex-none" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{t.truck_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[t.cuisine_type, t.service_area || t.city].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {t.claim_status === "verified" ? (
                    <span className="flex-none text-[10px] font-bold uppercase tracking-wide bg-green-100 text-green-700 rounded-full px-2 py-0.5">Claimed</span>
                  ) : (
                    <span className="flex-none text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary rounded-full px-2 py-0.5">Claim it</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mb-10">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-3">Around {m.region_label || m.city}</h2>
        <p className="text-sm text-muted-foreground max-w-3xl">
          TruckTrackr coverage around {m.in_phrase || m.city} reaches nearby communities including {m.nearby.join(", ")}.
          Trucks based anywhere in {m.region_label || m.city} often post stops across these towns — the{" "}
          <Link to={mapHref} onClick={() => trackSiteEvent("market_map_click", { market: m.slug })} className="text-primary hover:underline">live map</Link>{" "}
          always shows exactly where everyone is today.
        </p>
      </section>

      <section className="mb-10 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-2">Need a Food Truck for an Event in {m.in_phrase || m.city}?</h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
          Planning a wedding, festival, company lunch or party? Submit one request and reach participating local food trucks —
          they reply directly to you. New to hiring a truck? Read our guide on{" "}
          <Link to="/blog/how-to-hire-a-food-truck-for-an-event" className="text-primary hover:underline">how to hire a food truck for an event</Link>.
        </p>
        <Link to="/find-a-truck" onClick={() => trackSiteEvent("market_request_click", { market: m.slug })}>
          <Button data-testid="market-request-cta">Request a Food Truck</Button>
        </Link>
      </section>

      <section className="mb-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-2 flex items-center gap-2"><DeviceMobile size={18} /> Find {m.city} Food Trucks on the Go</h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
          Get the TruckTrackr app for live locations, schedules and notifications when your favorite {m.city} trucks post a stop.
          Wondering how it works? See{" "}
          <Link to="/blog/how-to-find-food-trucks-near-you" className="text-primary hover:underline">how to find food trucks near you</Link>.
        </p>
        <div className="flex flex-wrap gap-3">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackSiteEvent("market_appstore_click", { market: m.slug })} data-testid="market-appstore-link"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity">
             App Store
          </a>
          {ANDROID_LIVE && (
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackSiteEvent("market_playstore_click", { market: m.slug })} data-testid="market-playstore-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border bg-card text-sm font-semibold hover:border-primary/50 transition-colors">
              ▶ Google Play
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
